import React, { useEffect, useState, useRef } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { IoIosClose } from "react-icons/io";
import {
	PageContainer,
	InnerContentContainer,
	GreyContainer,
	ContentLoadingError,
} from "../../components";
import {
	LessonContent,
	CertificateButtons,
} from "../../components/features/CourseDetail";
import { ModuleCardWrapper } from "../../components/elements";
import {
	CourseDetailContainer,
	ShowModulesListButton,
	ModulesListWrapper,
	CloseModulesListButton,
	CloseModulesListButtonWrapper,
} from "./CourseDetailStyled";
import { QuizAnswerResponse } from "../../types/course";
import { useCourseDetail } from "../../context/courseDetail/courseDetailContext";
import { useVideoPlayer } from "../../hooks/useVideoPlayer";
import { useLessonRating } from "../../hooks/useLessonRating";
import { useDescriptionTruncation } from "../../hooks/useDescriptionTruncation";
import { useScrollPosition } from "../../hooks/useScrollPosition";
import loadingGif from "../../assets/images/loading.gif";

export interface ICourseContainerProps {
	hasOneButton?: boolean;
	hasTwoButton?: boolean;
}

/**
 * Main CourseDetail page component
 * Orchestrates the course detail view including:
 * - Modules sidebar navigation
 * - Video player
 * - Lesson content (description, materials, ratings)
 * - Quiz functionality
 * - Certificate/seal buttons
 */
const CourseDetail: React.FC = () => {
	const {
		loading,
		course,
		openModuleList,
		showModuleList,
		closeModuleList,
		activeModule,
		activeModuleClass,
		isCourseFinished,
		courseCertificateUrl,
		sealAvailable,
		sealUrl,
	} = useCourseDetail()!;

	// Quiz state
	const [previousAnswers, setPreviousAnswers] = useState<QuizAnswerResponse[]>([]);

	// Lesson transition state
	const [isTransitioning, setIsTransitioning] = useState(false);
	const previousLessonId = useRef<number | undefined>(undefined);

	// Video player hook
	const {
		playing,
		startTime,
		playerRef,
		handleStartVideo,
		handlePauseVideo,
		handleOnProgressVideo,
		pauseVideo,
		seekTo,
	} = useVideoPlayer({
		lessonId: activeModuleClass?.id,
		videoUrl: activeModuleClass?.videoUrl,
	});

	// Lesson rating hook
	const { lessonRating, lessonRatingKey, handleSessionRating } = useLessonRating({
		lessonId: activeModuleClass?.id,
		initialRating: activeModuleClass?.rating,
	});

	// Description truncation hook
	const { truncateDescription, showTruncateButton, toggleTruncateDescription } = useDescriptionTruncation({
		description: activeModuleClass?.description,
		hasVideo: !!activeModuleClass?.videoUrl,
	});

	// Scroll position hook for sidebar
	useScrollPosition();

	// Handle lesson transition loading state
	useEffect(() => {
		if (activeModuleClass?.id && previousLessonId.current !== undefined) {
			if (previousLessonId.current !== activeModuleClass.id) {
				setIsTransitioning(true);
				// Short delay to show the transition, then hide
				const timer = setTimeout(() => {
					setIsTransitioning(false);
				}, 300);
				return () => clearTimeout(timer);
			}
		}
		previousLessonId.current = activeModuleClass?.id;
	}, [activeModuleClass?.id]);

	// Reset quiz answers when lesson changes
	useEffect(() => {
		if (activeModuleClass) {
			if (activeModuleClass.isQuiz && activeModule?.quizObject) {
				setPreviousAnswers(activeModule.quizObject.avaliacoes_respondidas);
			} else {
				setPreviousAnswers([]);
			}
		}
	}, [activeModuleClass, activeModule]);

	const addAnswerToList = (answer: QuizAnswerResponse) => {
		setPreviousAnswers((prev) => [...prev, answer]);
	};

	// Determine if certificate/seal buttons should show
	const hasOneButton =
		isCourseFinished && course?.isComplete && (!!courseCertificateUrl || sealAvailable);
	const hasTwoButton = !!(
		sealAvailable &&
		isCourseFinished &&
		course?.isComplete &&
		courseCertificateUrl &&
		courseCertificateUrl !== "#" &&
		course?.hasCertificate
	);

	return (
		<PageContainer
			hideBanner
			pageTitle={course?.name}
			showBreadcrumb
			showSearch={false}
			searchHelpText="Neste curso"
		>
			{loading ? (
				<Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
					<img src={loadingGif} alt="Carregando..." />
				</Box>
			) : (
				<>
					{course ? (
						<>
							{/* Mobile button to show modules - must be outside GreyContainer */}
							<ShowModulesListButton onClick={() => openModuleList()}>
								Ver mais aulas
							</ShowModulesListButton>

							<GreyContainer>
								<InnerContentContainer>
									<CourseDetailContainer
										hasOneButton={hasOneButton}
										hasTwoButton={hasTwoButton}
									>
										{/* Left Column - Modules List */}
										<Box gridColumn="1" id="colunaModulos" className="colunaModulos">
											{course && (
												<CertificateButtons
													isCourseFinished={isCourseFinished}
													isCourseComplete={course.isComplete}
													courseCertificateUrl={courseCertificateUrl}
													hasCertificate={course.hasCertificate}
													sealAvailable={sealAvailable}
													sealUrl={sealUrl}
												/>
											)}
											{showModuleList && (
												<ModulesListWrapper>
													{window.innerWidth < 900 && (
														<CloseModulesListButtonWrapper>
															<CloseModulesListButton
																onClick={() => closeModuleList()}
															>
																<IoIosClose size={30} color="#FFF" />
															</CloseModulesListButton>
														</CloseModulesListButtonWrapper>
													)}
													{course.modules?.map((module) => (
														<ModuleCardWrapper
															hasComments={module.hasComments}
															showArrow
															key={module.id}
															module={module}
														/>
													))}
												</ModulesListWrapper>
											)}
										</Box>

										{/* Right Column - Main Content */}
										<Box
											gridColumn="2"
											gridRow="1/3"
											paddingLeft={{ xs: "0px", md: "21px" }}
											width="100%"
											position="relative"
										>
											{/* Transition loading overlay */}
											{isTransitioning && (
												<Box
													position="absolute"
													top={0}
													left={0}
													right={0}
													bottom={0}
													display="flex"
													alignItems="center"
													justifyContent="center"
													bgcolor="rgba(255, 255, 255, 0.8)"
													zIndex={100}
													sx={{ backdropFilter: "blur(2px)" }}
												>
													<CircularProgress
														size={40}
														aria-label="Carregando aula..."
													/>
												</Box>
											)}

											{!activeModule ? (
												<div role="alert">Nenhum módulo encontrado.</div>
											) : (
												<>
													{activeModule.name && activeModuleClass && (
														<LessonContent
															activeModuleClass={activeModuleClass}
															activeModule={activeModule}
															course={course}
															videoUrl={activeModuleClass.videoUrl}
															playing={playing}
															playerRef={playerRef}
															startTime={startTime}
															onStartVideo={handleStartVideo}
															onPauseVideo={handlePauseVideo}
															onProgressVideo={handleOnProgressVideo}
															pauseVideo={pauseVideo}
															seekTo={seekTo}
															lessonRating={lessonRating}
															lessonRatingKey={lessonRatingKey}
															onRate={handleSessionRating}
															truncateDescription={truncateDescription}
															showTruncateButton={showTruncateButton}
															onToggleTruncate={toggleTruncateDescription}
															previousAnswers={previousAnswers}
															onQuizAnswer={addAnswerToList}
														/>
													)}
												</>
											)}
										</Box>
									</CourseDetailContainer>
								</InnerContentContainer>
							</GreyContainer>
						</>
					) : (
						<GreyContainer>
							<InnerContentContainer>
								<ContentLoadingError
									refresh={() => window.location.reload()}
									loadedResourceErrorMsg="as aulas"
								/>
							</InnerContentContainer>
						</GreyContainer>
					)}
				</>
			)}
		</PageContainer>
	);
};

export default CourseDetail;
